import { formatWithCommasPreserveDecimals, unformatNumberString } from 'common/utils/formatNumber'
import { ShrimpHealthThreshold } from 'modules/notifications/api/types'
import { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react'
import palette from 'theme/palette'

import {
  LabelName,
  LabelGroup,
  THRESHOLD_VALUES,
  LABEL_COLOR_MAP,
  TEXT_TYPE_LABELS,
  ROW_HEIGHT_TEXTTYPE,
  ROW_HEIGHT_DEFAULT,
  GROUP_DIVIDER_BORDER,
} from '../constants'
import { tableStyles } from '../styles'
import {
  getLabelBackgroundColor,
  isLabelDisabled,
  normalizeLabel,
  isValueOutOfThreshold,
  getVibrioColorLimit,
} from '../utils'

const styleTextArea: React.CSSProperties = {
  ...tableStyles.textType,
  textAlign: 'center',
  verticalAlign: 'middle',
  overflowY: 'auto',
  resize: 'none' as const,
  lineHeight: '1.5',
  padding: 8,
}

interface OptionItem {
  id: number
  name: string
}

interface ThresholdAgeRange {
  age_from: number
  age_to: number
  min: number
  max: number
}

interface Threshold {
  parameter: string
  min: number
  max: number
  is_active: boolean
  age_ranges?: ThresholdAgeRange[]
}

export interface BootstrapData {
  medicine?: OptionItem[]
  vitamin?: OptionItem[]
  feed?: OptionItem[]
  foodBrands?: OptionItem[]
  waterColor?: OptionItem[]
  waterQualityThreshold?: { thresholds: Threshold[] }
}

interface EditableCellProps {
  value: number | string
  onChange: (value: string) => Promise<void>
  editable: boolean
  label: string
  group?: string
  id?: number
  columnIndex?: number
  bootstrap?: BootstrapData
  isMobile?: boolean
  // Last row of a section (e.g. ผลกุ้ง before ผลน้ำ): swaps the hairline bottom
  // border for the emphasized group divider so the split is easy to spot.
  hasGroupDivider?: boolean
  // Shrimp age (days) of this cell's column, for age-range water thresholds (NK-272).
  shrimpAge?: number
  // Shrimp-health alert settings; colour the vibrio cells (NK-273).
  shrimpHealthThresholds?: ShrimpHealthThreshold[]
}

const getCellStyle = (
  inputValue: string,
  editable: boolean,
  label: string,
  group?: string,
  bootstrap?: BootstrapData,
  isMobile?: boolean,
  hasGroupDivider?: boolean,
  shrimpAge?: number,
  shrimpHealthThresholds?: ShrimpHealthThreshold[]
) => {
  const labelLower = label.toLowerCase()
  let bgColor = palette.light.background.default
  let textColor = palette.light.text.primary

  const unformatted =
    inputValue === '-' || inputValue === null || inputValue === undefined
      ? ''
      : unformatNumberString(String(inputValue))
  const numValue = unformatted === '' ? NaN : Number(unformatted)
  const isValidNumber = !isNaN(numValue)

  if (isLabelDisabled(labelLower)) {
    bgColor = palette.light.grey[200]
  } else {
    let shouldShowColor = false
    const colorLimit = getVibrioColorLimit(label, shrimpHealthThresholds, shrimpAge)
    if (isValidNumber && colorLimit !== null) {
      if (colorLimit !== undefined) shouldShowColor = numValue > colorLimit
      // Settings not loaded yet.
      else if (
        labelLower === LabelName.ShrimpLiverYellow.toLowerCase() ||
        labelLower === LabelName.WaterYellow.toLowerCase() ||
        labelLower === LabelName.ShrimpStomachYellow.toLowerCase()
      )
        shouldShowColor = numValue > THRESHOLD_VALUES.YELLOW_THRESHOLD
      else shouldShowColor = numValue > THRESHOLD_VALUES.DEFAULT_THRESHOLD
    }

    if (shouldShowColor) {
      bgColor = getLabelBackgroundColor(label)
    } else if (!editable) {
      bgColor = palette.light.grey[200]
    }

    if (
      group === LabelGroup.WaterResult &&
      isValidNumber &&
      isValueOutOfThreshold(
        label,
        numValue,
        bootstrap?.waterQualityThreshold?.thresholds || [],
        shrimpAge
      )
    ) {
      textColor = palette.light.error.main
    }
  }

  return {
    width: '100%',
    height: `${ROW_HEIGHT_DEFAULT}px`,
    borderBottom: hasGroupDivider ? GROUP_DIVIDER_BORDER : `1.5px solid ${palette.light.grey[300]}`,
    borderTop: `0px solid ${palette.light.grey[300]}`,
    borderRight: `1.5px solid ${palette.light.grey[300]}`,
    borderLeft: `0px solid ${palette.light.grey[300]}`,
    outline: 'none',
    padding: '0 8px',
    fontSize: isMobile ? '1.625rem' : '0.875rem',
    fontWeight: isMobile ? 700 : undefined,
    lineHeight: `${ROW_HEIGHT_DEFAULT}px`,
    backgroundColor: bgColor,
    color: textColor,
    textAlign: 'center' as const,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  }
}

const isSelectField = (group: string, label: string) => {
  if (
    [LabelGroup.Drug, LabelGroup.Vitamin, LabelGroup.MicrobeFood, LabelGroup.BrandFood].includes(
      group as LabelGroup
    )
  )
    return true
  if (group === LabelGroup.WaterResult && label === LabelName.WaterColor) return true
  return false
}

const getSelectOptions = (group: string, bootstrap?: BootstrapData): OptionItem[] => {
  switch (group) {
    case LabelGroup.Drug:
      return bootstrap?.medicine ?? []
    case LabelGroup.Vitamin:
      return bootstrap?.vitamin ?? []
    case LabelGroup.MicrobeFood:
      return bootstrap?.feed ?? []
    case LabelGroup.BrandFood:
      return bootstrap?.foodBrands ?? []
    case LabelGroup.WaterResult:
      return bootstrap?.waterColor ?? []
    default:
      return []
  }
}

export const EditableCell = memo(
  ({
    value,
    onChange,
    editable,
    label,
    group,
    id,
    columnIndex,
    bootstrap,
    hasGroupDivider,
    shrimpAge,
    shrimpHealthThresholds,
  }: EditableCellProps & { bootstrap?: BootstrapData }) => {
    const [inputValue, setInputValue] = useState(value ?? '')
    const isMountedRef = useRef(true)
    const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true'

    const isTextField = useMemo(() => {
      const norm = normalizeLabel(String(label))
      return TEXT_TYPE_LABELS.some((l) => normalizeLabel(String(l)) === norm)
    }, [label])

    const isIntegerOnly = useMemo(
      () => normalizeLabel(String(label)) in LABEL_COLOR_MAP && !isTextField,
      [label, isTextField]
    )

    useEffect(() => {
      if (value === '' || value === null || value === undefined) {
        setInputValue('-')
      } else {
        if (isTextField) {
          setInputValue(String(value))
        } else {
          setInputValue(formatWithCommasPreserveDecimals(String(value)))
        }
      }
    }, [value])

    // NK-305: latest typed value for the unmount flush below.
    const pendingRef = useRef({ inputValue, value, onChange, isTextField })
    pendingRef.current = { inputValue, value, onChange, isTextField }
    // True between a keystroke and the save it leads to, so the unmount flush
    // only ever sends what the user typed (rows unmount while scrolling too).
    const isDirtyRef = useRef(false)

    useEffect(() => {
      return () => {
        isMountedRef.current = false
        // NK-305: a value typed but never blurred (tab switch, row virtualised
        // away, page closing) used to be dropped. Send it as blur would have.
        if (!isDirtyRef.current) return
        const pending = pendingRef.current
        const display = String(pending.inputValue).trim()
        if (display === '' && pending.value === '-') return
        const payload = pending.isTextField ? display : unformatNumberString(display)
        if (payload !== String(pending.value)) {
          pending.onChange(payload).catch(() => undefined)
        }
      }
    }, [])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let raw = e.target.value
        isDirtyRef.current = true

        if (isTextField) {
          setInputValue(raw === '' ? '-' : raw)
          return
        }

        const unformatted = unformatNumberString(raw)

        if (isIntegerOnly) {
          if (unformatted !== '' && !/^\d*$/.test(unformatted)) return
          let intRaw = unformatted
          if (intRaw.length > 1 && intRaw.startsWith('0')) {
            intRaw = intRaw.replace(/^0+/, '') || '0'
          }
          const formatted = formatWithCommasPreserveDecimals(intRaw)
          setInputValue(formatted === '' ? '-' : formatted)
        } else {
          if (unformatted !== '' && !/^(\d+(\.\d*)?|\.\d*)$/.test(unformatted)) return
          const formatted = formatWithCommasPreserveDecimals(unformatted)
          setInputValue(formatted === '' ? '-' : formatted)
        }
      },
      [isIntegerOnly, isTextField]
    )

    const handleSelectChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value
        setInputValue(newValue)
        onChange(newValue).catch(() => {
          if (isMountedRef.current) {
            setInputValue(String(value))
          }
        })
      },
      [onChange, value]
    )

    const handleBlur = useCallback(() => {
      isDirtyRef.current = false
      let displayValue = String(inputValue).trim()
      let payload = displayValue

      if (payload === '' && value === '-') return

      if (!isTextField) {
        payload = unformatNumberString(displayValue)
      }

      if (payload !== String(value)) {
        onChange(payload).catch(() => {
          if (isMountedRef.current) {
            if (isTextField) {
              setInputValue(String(value || '-'))
            } else {
              setInputValue(formatWithCommasPreserveDecimals(String(value || '')))
            }
          }
        })
      }
    }, [inputValue, value, onChange])

    const cellStyle = useMemo(
      () =>
        getCellStyle(
          String(inputValue),
          editable,
          label,
          group,
          bootstrap,
          isMobile,
          hasGroupDivider,
          shrimpAge,
          shrimpHealthThresholds
        ),
      [
        inputValue,
        editable,
        label,
        group,
        bootstrap,
        isMobile,
        hasGroupDivider,
        shrimpAge,
        shrimpHealthThresholds,
      ]
    )

    if (!editable || isLabelDisabled(label)) {
      return <div style={cellStyle}>{inputValue === '' ? '-' : inputValue}</div>
    }

    const isSelect = isSelectField(group || '', label)
    if (isSelect) {
      const options = getSelectOptions(group || '', bootstrap)

      return (
        <select
          value={inputValue === '-' ? '' : inputValue}
          onChange={handleSelectChange}
          style={{
            ...cellStyle,
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
          }}
        >
          <option value="">-</option>
          {options.map((option: OptionItem, idx: number) => (
            <option key={idx} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      )
    }

    if (isTextField) {
      return (
        <textarea
          value={inputValue === '-' ? '' : inputValue}
          onChange={(e) => {
            isDirtyRef.current = true
            setInputValue(e.target.value === '' ? '-' : e.target.value)
          }}
          onBlur={handleBlur}
          style={{ ...cellStyle, ...styleTextArea }}
          rows={3}
          data-group={group}
          data-label={label}
          data-id={id}
          data-column-index={columnIndex}
          placeholder="-"
        />
      )
    }

    return (
      <input
        type={'text'}
        value={inputValue === '-' ? '' : formatWithCommasPreserveDecimals(inputValue)}
        onChange={handleChange}
        onBlur={handleBlur}
        step={isIntegerOnly ? '1' : 'any'}
        style={cellStyle}
        data-group={group}
        data-label={label}
        data-id={id}
        data-column-index={columnIndex}
        placeholder="-"
      />
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.editable === nextProps.editable &&
      prevProps.label === nextProps.label &&
      prevProps.group === nextProps.group &&
      prevProps.id === nextProps.id &&
      prevProps.bootstrap === nextProps.bootstrap &&
      prevProps.hasGroupDivider === nextProps.hasGroupDivider &&
      prevProps.shrimpAge === nextProps.shrimpAge &&
      prevProps.shrimpHealthThresholds === nextProps.shrimpHealthThresholds
    )
  }
)

EditableCell.displayName = 'EditableCell'
